import React, { useEffect, useMemo, FC } from 'react'
import { useMarket } from 'src/hooks/useMarket'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { usePage } from 'src/hooks/usePage'
import { EVENTS } from './constants'

interface ChatbotTrackingProps {
  purchaseId: string
}

const ChatbotTracking = <P extends object>(
  WrappedComponent: React.ComponentType<P>
): FC<P & ChatbotTrackingProps> => {
  const Tracking: FC<P & ChatbotTrackingProps> = props => {
    const { purchaseId = '' } = props
    const { pageId } = usePage()
    const { chatbot } = useMarket()

    const section = SECTIONS.CHATBOT

    const { sendEvent: sendKafkaEventOpenChat } = useKafkaEvent({
      eventName: 'open_chat',
      page: pageId,
    })
    const { sendEvent: sendKafkaEventStartChat } = useKafkaEvent({
      eventName: 'start_chat',
      page: pageId,
    })

    const eventSenders = useMemo(
      () => ({
        open_chat: sendKafkaEventOpenChat,
        start_chat: sendKafkaEventStartChat,
      }),
      [sendKafkaEventOpenChat, sendKafkaEventStartChat]
    )

    useEffect(() => {
      if (typeof window === 'undefined') {
        // running on server
        return () => {
          // no-op: server environment
        }
      }

      let handleClick: ((event: Event) => void) | null = null

      if (chatbot) {
        handleClick = (event: Event) => {
          const customEvent = event as CustomEvent<{ type: string }>
          const eventName = EVENTS[customEvent.detail.type]
          if (!eventName) return

          const sendEvent = eventSenders[eventName as keyof typeof eventSenders]

          if (sendEvent) {
            sendEvent({ section }, { purchaseId })
          }
        }

        document.addEventListener('kindly:ui', handleClick as EventListener)
      }

      return () => {
        if (handleClick) {
          document.removeEventListener('kindly:ui', handleClick as EventListener)
        }
      }
    }, [chatbot, purchaseId, eventSenders, section])

    return <WrappedComponent {...props} />
  }

  return Tracking
}

export default ChatbotTracking
